/**
 * payments-api — typed wrappers for the PUBLIC payment data layer, through the
 * @v0y4ge/api-client `apiRaw` choke point (same facade pattern as wf-api.ts).
 *
 * The PayPal ops are DECLARED (REQ-045 in CLAUDE-CODE-REQUESTS.md; shapes in the
 * 2026-07-20 changelog) and served by the preview harness's __source-marked seed
 * layer until they land in @v0y4ge/api-mocks / the real backend. The Stripe ops
 * (createEventPaymentIntent / getEventPaymentStatus) already live in api-client
 * and are untouched — PayPal is purely additive.
 *
 * Money rules (REQ-036 posture, unchanged): the FE NEVER computes or sends
 * amount, currency, payee, or fee — the server derives everything from
 * eventId + ticketId + quantity. Amounts rendered come only from op responses.
 *
 * SECURITY: `orderAccessToken` is the per-order capability (IDOR guard). It is
 * response-body/state only — NEVER logged, never put in analytics. Capture
 * sends it in the POST body; the status GET mirrors the existing Stripe status
 * op's `?token=` shape so the two providers stay contract-symmetric.
 */
import { apiRaw, billing } from "@v0y4ge/api-client";
import { reportOpSource } from "../shell/opSource";

/** Idempotency-key generator — api-client's own (billing namespace export). */
export const newIdempotencyKey = billing.newIdempotencyKey;

/** Data-source transparency (2026-07-16 convention): report every op result. */
const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export type PayPalOrderStatus = "pending" | "paid" | "failed" | "canceled";

export interface PayPalOrderResult {
  orderId: string;
  status: PayPalOrderStatus;
  amountMinor: number; // server-derived, minor units
  currency: string;    // server-derived ISO 4217
}

export interface PayPalOrderCreateResult extends PayPalOrderResult {
  orderAccessToken: string;      // per-order capability — never log
  approveUrl?: string | null;    // redirect fallback; merchantIdInPayPal on return is display-only
}

/** PayPal JS SDK load config — from the event's public payment config, never hardcoded. */
export interface PayPalSdkConfig {
  clientId: string;   // partner client id
  merchantId: string; // seller merchant id
  bnCode?: string;    // data-partner-attribution-id
  currency: string;
}

export interface EventPublicPaymentConfig {
  provider: "stripe" | "paypal" | "adyen" | null; // the event's configured provider
  verified: boolean;                    // configured + provider-confirmed receivable
  currency?: string;
  paypal?: PayPalSdkConfig | null;      // present only when provider === "paypal" && verified
  // Adyen (REQ-060) carries no SDK params here — everything the Drop-in needs
  // (sessionData / clientKey / environment) comes from createEventAdyenSession.
}

/** GET the event's public payment config (provider chooser + SDK params). */
export const getEventPublicPaymentConfig = (eventId: string) =>
  tag("getEventPublicPaymentConfig", apiRaw<EventPublicPaymentConfig>(
    `/v1/public/events/${encodeURIComponent(eventId)}/payment-config`, undefined, 'getEventPublicPaymentConfig'));

/** POST — server creates the PayPal order and derives amount/currency itself. */
export const createEventPayPalOrder = (input: { eventId: string; ticketId?: string; quantity?: number; idempotencyKey: string }) => {
  const { eventId, ...body } = input;
  return tag("createEventPayPalOrder", apiRaw<PayPalOrderCreateResult>(
    `/v1/public/events/${encodeURIComponent(eventId)}/paypal/orders`,
    { method: "POST", body: JSON.stringify(body) }, 'createEventPayPalOrder'));
};

/** POST — SERVER-side capture after buyer approval (distinct from Stripe's confirm-on-client). */
export const captureEventPayPalOrder = (eventId: string, orderId: string, orderAccessToken: string) =>
  tag("captureEventPayPalOrder", apiRaw<PayPalOrderResult>(
    `/v1/public/events/${encodeURIComponent(eventId)}/paypal/orders/${encodeURIComponent(orderId)}/capture`,
    { method: "POST", body: JSON.stringify({ orderAccessToken }) }, 'captureEventPayPalOrder'));

/** GET — poll for async captures until terminal. */
export const getEventPayPalOrderStatus = (eventId: string, orderId: string, orderAccessToken: string) =>
  tag("getEventPayPalOrderStatus", apiRaw<PayPalOrderResult>(
    `/v1/public/events/${encodeURIComponent(eventId)}/paypal/orders/${encodeURIComponent(orderId)}?token=${encodeURIComponent(orderAccessToken)}`, undefined, 'getEventPayPalOrderStatus'));

/* ── Adyen checkout (REQ-060) — session-driven Drop-in, NOT a client_secret
 * Element and NOT Smart Buttons. Ops mirror the shipped Stripe pair one-to-one
 * (`createEventAdyenSession` ~ createEventPaymentIntent; the status op requires
 * the `orderAccessToken` as ?token= and 404s on mismatch — IDOR guard).
 * `sessionData` is response-body-only — NEVER logged. Adyen is async: poll the
 * status op for the authoritative "paid"; onPaymentCompleted alone is not it. */
export type AdyenPaymentStatus = "pending" | "paid" | "failed" | "canceled";

export interface AdyenSessionResult {
  sessionId: string;              // Adyen session id (Stripe analog: paymentIntentId)
  sessionData: string;            // Drop-in init payload — NEVER logged
  clientKey: string;              // Adyen Web client key (required by Drop-in)
  environment: "test" | "live";   // Drop-in env — TEST badge when "test"
  amountMinor: number;            // server-computed, minor units
  currency: string;               // server-computed ISO 4217
  orderAccessToken: string;       // per-order capability — never log
  status: AdyenPaymentStatus;
}

export interface AdyenSessionStatusResult {
  sessionId: string;
  status: AdyenPaymentStatus;
  amountMinor: number;
  currency: string;
}

/** POST — server creates the Adyen session; NO amount/splits/account ids in the
 *  request (server derives price/fee/routing from eventId+ticketId+quantity). */
export const createEventAdyenSession = (input: { eventId: string; ticketId?: string; quantity?: number; idempotencyKey: string }) => {
  const { eventId, ...body } = input;
  return tag("createEventAdyenSession", apiRaw<AdyenSessionResult>(
    `/v1/public/events/${encodeURIComponent(eventId)}/adyen/sessions`,
    { method: "POST", body: JSON.stringify(body) }, 'createEventAdyenSession'));
};

/** GET — poll until terminal; the authoritative "paid" for async Adyen flows. */
export const getEventAdyenPaymentStatus = (eventId: string, sessionId: string, orderAccessToken: string) =>
  tag("getEventAdyenPaymentStatus", apiRaw<AdyenSessionStatusResult>(
    `/v1/public/events/${encodeURIComponent(eventId)}/adyen/sessions/${encodeURIComponent(sessionId)}?token=${encodeURIComponent(orderAccessToken)}`, undefined, 'getEventAdyenPaymentStatus'));

/** Render helper for server-derived minor-unit amounts. Display only. */
export function formatMinor(amountMinor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amountMinor / 100);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency}`;
  }
}
